import { useState } from "react";
import { Clock, CreditCard, UserRound, XCircle } from "lucide-react";
import { allocateInterviewRequest, InterviewSlot, listAdminInterviewSlots, updateInterviewRequest } from "../../Lib/interviewApi";
import { setAdminMessageResolved } from "../../Lib/adminMessagesApi";

export type InterviewRequestRow = {
    id: string;
    studentName?: string;
    studentEmail?: string;
    studentMobile?: string;
    tier?: string;
    packageLabel?: string;
    targetRole?: string;
    notes?: string;
    preferredDates?: string[];
    preferredTime?: string;
    timezone?: string;
    experienceLevel?: string;
    companyPreference?: string;
    status?: string;
    source?: string;
    slotId?: string;
    interviewerName?: string;
    scheduledAt?: any;
    paymentStatus?: string;
    needsAdminReview?: boolean;
};

const dateLabel = (value: any) => {
    const date = value?.toDate?.() || (value ? new Date(value) : null);
    return date && !Number.isNaN(date.getTime()) ? date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "Not allocated";
};

export default function InterviewRequestMessage({ messageId, request, slots, onSlotsChanged, onNotice }: {
    messageId: string;
    request?: InterviewRequestRow;
    slots: InterviewSlot[];
    onSlotsChanged: (slots: InterviewSlot[]) => void;
    onNotice: (message: string) => void;
}) {
    const [slotId, setSlotId] = useState("");
    const [saving, setSaving] = useState(false);

    if (!request) return <p className="rounded-xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">The related interview request is not available yet.</p>;

    const compatible = slots.filter((slot) => !request.tier || slot.tier === request.tier);
    const canAllocate = request.source !== "DIRECT_BOOKING" && ["slot_requested", "pending", "awaiting_payment", "payment_failed"].includes(request.status || "");
    const canReject = request.source !== "DIRECT_BOOKING" && ["pending", "slot_requested", "payment_failed"].includes(request.status || "");
    const statusTone = request.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : request.status === "completed" ? "bg-blue-100 text-blue-700" : request.status === "rejected" || request.status === "payment_review" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800";

    const allocate = async () => {
        if (!slotId) return onNotice("Choose an available slot first.");
        setSaving(true); onNotice("");
        try {
            await allocateInterviewRequest({ interviewRequestId: request.id, slotId });
            await setAdminMessageResolved(messageId, true);
            onNotice(`A payment-ready slot was sent to ${request.studentName || request.studentEmail}.`);
            onSlotsChanged(await listAdminInterviewSlots());
        } catch (cause: any) {
            onNotice(cause?.message || "The slot could not be allocated.");
        } finally { setSaving(false); }
    };

    const reject = async () => {
        setSaving(true); onNotice("");
        try {
            await updateInterviewRequest({ interviewRequestId: request.id, status: "rejected" });
            await setAdminMessageResolved(messageId, true);
            onNotice("Request rejected and marked resolved.");
        } catch (cause: any) {
            onNotice(cause?.message || "The request could not be rejected.");
        } finally { setSaving(false); }
    };

    return <section className="rounded-2xl border border-blue-100 bg-blue-50/50 p-4">
        <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-black text-slate-900">{request.studentName || "Student"}</h4>
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold capitalize ${statusTone}`}>{(request.status || "pending").replaceAll("_", " ")}</span>
            {request.needsAdminReview && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700">Admin review required</span>}
        </div>
        <p className="mt-1 text-sm text-slate-500">{[request.studentEmail, request.studentMobile].filter(Boolean).join(" · ")}</p>
        <div className="mt-3 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
            <span><strong>Package:</strong> {request.packageLabel || request.tier || "—"}</span>
            <span><strong>Role:</strong> {request.targetRole || "—"}</span>
            <span><strong>Preferred date:</strong> {request.preferredDates?.join(", ") || "Not specified"}</span>
            <span><strong>Timezone:</strong> {request.timezone || "—"}</span>
            <span><strong>Experience:</strong> {request.experienceLevel || "—"}</span>
            <span><strong>Company:</strong> {request.companyPreference || "—"}</span>
        </div>
        {request.notes && <p className="mt-3 rounded-xl bg-white p-3 text-sm text-slate-700"><strong>Notes:</strong> {request.notes}</p>}
        {request.needsAdminReview && <p className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">The assigned interviewer was deactivated or lost the required tier role. Keep the confirmed booking intact and coordinate any replacement offline.</p>}

        {canAllocate ? <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
            <label className="text-xs font-bold text-slate-600">Compatible interviewer slot
                <select value={slotId} onChange={(event) => setSlotId(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900">
                    <option value="">Choose a slot</option>
                    {compatible.map((slot) => <option key={slot.id} value={slot.id}>{slot.interviewerName} · {dateLabel(slot.start)} · {slot.tier}</option>)}
                </select>
            </label>
            {!compatible.length && <p className="mt-2 text-xs font-semibold text-amber-700">No compatible published interviewer slots. Coordinate offline, then ask the interviewer to publish one.</p>}
            <div className="mt-3 flex flex-wrap gap-2">
                <button disabled={saving || !slotId} onClick={allocate} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"><UserRound size={16} />{request.status === "awaiting_payment" ? "Reallocate slot" : "Allocate and request payment"}</button>
                {canReject && <button disabled={saving} onClick={reject} className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-4 py-2 text-sm font-bold text-red-700 disabled:opacity-50"><XCircle size={16} />Reject</button>}
            </div>
        </div> : <div className="mt-4 grid gap-2 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700 sm:grid-cols-3">
            <p className="flex items-center gap-2"><Clock size={15} />{dateLabel(request.scheduledAt)}</p>
            <p className="flex items-center gap-2"><UserRound size={15} />{request.interviewerName || "Interviewer pending"}</p>
            <p className="flex items-center gap-2"><CreditCard size={15} />Payment: {request.paymentStatus || (request.status === "confirmed" ? "paid" : "pending")}</p>
        </div>}
    </section>;
}
